import { useState, useEffect } from 'react'
import { Card, Table, Button, Modal, Form, Input, InputNumber, Select, Tag, Space, message, Statistic, Row, Col, Popconfirm } from 'antd'
import { BankOutlined, PlusOutlined } from '@ant-design/icons'
import { financeApi } from '../../services/api'

const accountTypeMap: Record<number, { label: string; color: string }> = {
  1: { label: '银行账户', color: 'blue' },
  2: { label: '微信支付', color: 'green' },
  3: { label: '支付宝', color: 'cyan' },
  4: { label: '其他', color: 'default' }
}

export default function BankAccounts() {
  const [accounts, setAccounts] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingAccount, setEditingAccount] = useState<any>(null)
  const [form] = Form.useForm()

  useEffect(() => { loadAccounts() }, [])

  const loadAccounts = async () => {
    setLoading(true)
    try {
      const res = await financeApi.getBankAccounts()
      const data = res.data || res
      setAccounts(Array.isArray(data) ? data : [])
    } catch (e) { console.error(e); message.error('加载失败') }
    setLoading(false)
  }

  const handleAdd = () => {
    setEditingAccount(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (record: any) => {
    setEditingAccount(record)
    form.setFieldsValue(record)
    setModalVisible(true)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (editingAccount) {
        await financeApi.updateBankAccount(editingAccount.id, values)
        message.success('更新成功')
      } else {
        await financeApi.createBankAccount(values)
        message.success('创建成功')
      }
      setModalVisible(false)
      loadAccounts()
    } catch (e) { console.error(e) }
  }

  const handleDelete = async (id: number) => {
    try {
      await financeApi.deleteBankAccount(id)
      message.success('删除成功')
      loadAccounts()
    } catch (e) { message.error('删除失败') }
  }

  const handleToggleStatus = async (record: any) => {
    try {
      await financeApi.updateBankAccount(record.id, { status: record.status === 1 ? 0 : 1 })
      message.success(record.status === 1 ? '已禁用' : '已启用')
      loadAccounts()
    } catch (e) { message.error('操作失败') }
  }

  const totalBalance = accounts.filter(a => a.status === 1).reduce((sum, a) => sum + Number(a.current_balance || 0), 0)

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '类型', dataIndex: 'account_type', width: 100, render: (v: number) => { const t = accountTypeMap[v]; return t ? <Tag color={t.color}>{t.label}</Tag> : v } },
    { title: '账户名称', dataIndex: 'account_name', width: 150 },
    { title: '账号/商户号', dataIndex: 'account_number', width: 160, render: (v: string, r: any) => v || r.merchant_id || '-' },
    { title: '开户行', dataIndex: 'bank_name', width: 140, render: (v: string) => v || '-' },
    { title: '期初余额', dataIndex: 'initial_balance', width: 120, render: (v: number) => `¥${Number(v || 0).toFixed(2)}` },
    { title: '当前余额', dataIndex: 'current_balance', width: 120, render: (v: number) => <span style={{ fontWeight: 600 }}>¥{Number(v || 0).toFixed(2)}</span> },
    { title: '状态', dataIndex: 'status', width: 80, render: (v: number) => v === 1 ? <Tag color="green">正常</Tag> : <Tag color="red">禁用</Tag> },
    { title: '备注', dataIndex: 'remark', ellipsis: true, render: (v: string) => v || '-' },
    {
      title: '操作', width: 180,
      render: (_: any, record: any) => (
        <Space size="small">
          <Button size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Button size="small" onClick={() => handleToggleStatus(record)}>
            {record.status === 1 ? '禁用' : '启用'}
          </Button>
          <Popconfirm title="确认删除？" onConfirm={() => handleDelete(record.id)}>
            <Button size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card size="small">
            <Statistic title="活跃账户" value={accounts.filter(a => a.status === 1).length} suffix="个" prefix={<BankOutlined />} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="资金总额" value={totalBalance} precision={2} prefix="¥" valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic title="账户总数" value={accounts.length} suffix="个" />
          </Card>
        </Col>
      </Row>

      <Card title={<span><BankOutlined /> 银行账户管理</span>} extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增账户</Button>}>
        <Table columns={columns} dataSource={accounts} rowKey="id" loading={loading} size="middle" />
      </Card>

      <Modal title={editingAccount ? '编辑账户' : '新增账户'} open={modalVisible} onOk={handleSubmit} onCancel={() => setModalVisible(false)} width={520}>
        <Form form={form} layout="vertical">
          <Form.Item name="account_type" label="账户类型" rules={[{ required: true, message: '请选择' }]}>
            <Select placeholder="请选择账户类型">
              {Object.entries(accountTypeMap).map(([k, v]) => (
                <Select.Option key={k} value={Number(k)}>{v.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="account_name" label="账户名称" rules={[{ required: true, message: '请输入' }]}>
            <Input placeholder="如：中国银行对公账户" />
          </Form.Item>
          <Form.Item name="account_number" label="账号">
            <Input placeholder="银行卡号或商户号" />
          </Form.Item>
          <Form.Item name="bank_name" label="开户行">
            <Input placeholder="如：中国银行上海市闵行支行" />
          </Form.Item>
          <Form.Item name="merchant_id" label="商户号">
            <Input placeholder="微信/支付宝商户号" />
          </Form.Item>
          <Form.Item name="initial_balance" label="期初余额" initialValue={0}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} prefix="¥" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
